import {
  ClockCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Modal,
  Row,
  Select,
  Space,
  Tabs,
  Typography,
  Tooltip,
  message,
} from 'antd';
import { DayOfWeek, PostingTimeSlot } from '@monorepo/types';
import React, {
  useImperativeHandle,
  useState,
  forwardRef,
  useRef,
} from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export interface PostingScheduleModalActions {
  open: () => void;
}

interface Props {
  form: UseFormReturn<any>;
  videosPerDay: number;
}

const dayKeys: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

const hourOptions = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: h.toString().padStart(2, '0'),
}));
const minuteOptions = Array.from({ length: 12 }, (_, m) => ({
  value: m * 5,
  label: (m * 5).toString().padStart(2, '0'),
}));

const PostingScheduleModal = forwardRef<PostingScheduleModalActions, Props>(
  ({ form, videosPerDay }, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [activeDay, setActiveDay] = useState<DayOfWeek>(DayOfWeek.Monday);

    const days: { key: DayOfWeek; label: string }[] = [
      {
        key: DayOfWeek.Monday,
        label: t('seriesForm.postingSchedule.days.Mon'),
      },
      {
        key: DayOfWeek.Tuesday,
        label: t('seriesForm.postingSchedule.days.Tue'),
      },
      {
        key: DayOfWeek.Wednesday,
        label: t('seriesForm.postingSchedule.days.Wed'),
      },
      {
        key: DayOfWeek.Thursday,
        label: t('seriesForm.postingSchedule.days.Thu'),
      },
      {
        key: DayOfWeek.Friday,
        label: t('seriesForm.postingSchedule.days.Fri'),
      },
      {
        key: DayOfWeek.Saturday,
        label: t('seriesForm.postingSchedule.days.Sat'),
      },
      {
        key: DayOfWeek.Sunday,
        label: t('seriesForm.postingSchedule.days.Sun'),
      },
    ];

    useImperativeHandle(ref, () => ({
      open: () => setOpen(true),
    }));

    const schedule = form.watch('postingSchedule');

    // Resets all days to empty array
    const handleResetAll = () => {
      Modal.confirm({
        title: t('seriesForm.postingSchedule.resetConfirmTitle'),
        content: t('seriesForm.postingSchedule.resetConfirmContent'),
        okText: t('seriesForm.postingSchedule.resetAll'),
        okType: 'danger',
        onOk: () => {
          days.forEach(({ key }) =>
            form.setValue(`postingSchedule.${key}`, [])
          );
          message.success(t('seriesForm.postingSchedule.resetSuccess'));
        },
      });
    };

    const handleAddSlot = (day: DayOfWeek) => {
      const slots: PostingTimeSlot[] = schedule?.[day] || [];
      if (slots.length < videosPerDay) {
        form.setValue(`postingSchedule.${day}`, [
          ...slots,
          { hour: 12, minute: 0 },
        ]);
      }
    };

    const handleUpdateSlot = (
      day: DayOfWeek,
      index: number,
      field: keyof PostingTimeSlot,
      value: number
    ) => {
      const slots = [...(schedule?.[day] || [])];
      slots[index][field] = value;
      form.setValue(`postingSchedule.${day}`, slots);
    };

    const handleDeleteSlot = (day: DayOfWeek, index: number) => {
      const slots = [...(schedule?.[day] || [])];
      slots.splice(index, 1);
      form.setValue(`postingSchedule.${day}`, slots);
    };

    return (
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        width={540}
        title={
          <Space>
            <ClockCircleOutlined />
            <span>{t('seriesForm.postingSchedule.title')}</span>
          </Space>
        }
        okText={t('seriesForm.postingSchedule.save')}
        footer={[
          <Button
            key="reset"
            icon={<ReloadOutlined />}
            danger
            style={{ marginRight: 'auto' }}
            onClick={handleResetAll}
          >
            {t('seriesForm.postingSchedule.resetAll')}
          </Button>,
          <Button key="cancel" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </Button>,
          <Button key="ok" type="primary" onClick={() => setOpen(false)}>
            {t('seriesForm.postingSchedule.save')}
          </Button>,
        ]}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 18 }}
        >
          <Col>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('seriesForm.postingSchedule.chooseUpTo')} <b>{videosPerDay}</b>{' '}
              {videosPerDay > 1
                ? t('seriesForm.postingSchedule.timesPerDayPlural')
                : t('seriesForm.postingSchedule.timesPerDay')}
            </Text>
          </Col>
          <Col>
            <Tabs
              activeKey={activeDay}
              onChange={(k) => setActiveDay(k as DayOfWeek)}
              items={days.map((d) => ({
                label: d.label,
                key: d.key,
              }))}
              type="card"
              size="small"
              style={{ marginBottom: 0 }}
            />
          </Col>
        </Row>
        <Divider style={{ margin: '0 0 18px 0' }} />
        {/* Main time list for activeDay */}
        <div style={{ minHeight: 120 }}>
          {(schedule?.[activeDay] || []).length === 0 && (
            <Text type="secondary">
              {t('seriesForm.postingSchedule.noTimesSet')}
            </Text>
          )}
          {(schedule?.[activeDay] || []).map((slot, idx) => {
            if (idx + 1 > videosPerDay) {
              return null;
            }

            return (
              <Row
                key={idx}
                gutter={10}
                align="middle"
                style={{ marginBottom: 12 }}
              >
                <Col>
                  <Select
                    value={slot.hour}
                    style={{ width: 60 }}
                    options={hourOptions}
                    onChange={(val) =>
                      handleUpdateSlot(activeDay, idx, 'hour', val)
                    }
                    aria-label="Select hour"
                  />
                </Col>
                <Col>:</Col>
                <Col>
                  <Select
                    value={slot.minute}
                    style={{ width: 60 }}
                    options={minuteOptions}
                    onChange={(val) =>
                      handleUpdateSlot(activeDay, idx, 'minute', val)
                    }
                    aria-label="Select minute"
                  />
                </Col>
                <Col>
                  <Tooltip
                    title={t('seriesForm.postingSchedule.deleteTooltip')}
                  >
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSlot(activeDay, idx)}
                      type="text"
                      danger
                    />
                  </Tooltip>
                </Col>
              </Row>
            );
          })}
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleAddSlot(activeDay)}
            disabled={(schedule?.[activeDay]?.length || 0) >= videosPerDay}
            style={{ marginTop: 8 }}
            block
          >
            {t('seriesForm.postingSchedule.addTime')}
          </Button>
        </div>
      </Modal>
    );
  }
);

export default PostingScheduleModal;
